/**
 * Hooks 管理器
 * 提供可扩展的钩子机制，允许项目自定义 SDK 行为
 */

/** Hook 函数类型 */
export type HookFn<T = unknown, R = T> = (data: T) => R | Promise<R>

/** Hook 配置 */
export interface HookConfig {
  /** 认证相关 hooks */
  auth?: {
    beforeLogin?: HookFn<LoginParams, LoginParams>
    afterLogin?: HookFn<LoginResult, void>
    onLoginError?: HookFn<Error, void>
    beforeLogout?: HookFn<void, void>
    afterLogout?: HookFn<void, void>
    beforeSendCode?: HookFn<SendCodeParams, SendCodeParams>
    afterSendCode?: HookFn<void, void>
  }
  /** 用户相关 hooks */
  user?: {
    afterGetUserInfo?: HookFn<UserInfo, void>
    afterGetVipInfo?: HookFn<VipInfo, void>
  }
  /** 支付相关 hooks */
  payment?: {
    beforeCreateOrder?: HookFn<CreateOrderParams, CreateOrderParams>
    afterCreateOrder?: HookFn<Order, void>
    afterPaymentSuccess?: HookFn<Order, void>
    onPaymentFailed?: HookFn<Error, void>
  }
  /** 邀请相关 hooks */
  invite?: {
    afterGetRewardDetail?: HookFn<RewardDetail, void>
    afterApplyWithdraw?: HookFn<void, void>
  }
  /** 请求相关 hooks */
  request?: {
    beforeRequest?: HookFn<RequestConfig, RequestConfig>
    afterResponse?: HookFn<Response, Response>
    onRequestError?: HookFn<Error, void>
  }
}

/** 登录参数 */
interface LoginParams {
  phone?: string
  code?: string
  [key: string]: unknown
}

/** 登录结果 */
interface LoginResult {
  token: string
  userInfo?: unknown
}

/** 发送验证码参数 */
interface SendCodeParams {
  phone: string
  captcha_key?: string
  captcha_code?: string
  slide_x?: number
  slide_y?: number
  smsType?: string
}

/** 用户信息 */
interface UserInfo {
  [key: string]: unknown
}

/** VIP 信息 */
interface VipInfo {
  [key: string]: unknown
}

/** 创建订单参数 */
interface CreateOrderParams {
  packageId: number
  [key: string]: unknown
}

/** 订单 */
interface Order {
  id: string
  [key: string]: unknown
}

/** 佣金详情 */
interface RewardDetail {
  [key: string]: unknown
}

/** 请求配置 */
interface RequestConfig {
  url: string
  method: string
  headers?: Record<string, string>
  data?: unknown
}

/** 响应 */
interface Response {
  data: unknown
  status: number
}

/**
 * Hooks 管理器类
 */
export class HookManager {
  private hooks: HookConfig = {}

  constructor(config?: HookConfig) {
    if (config) {
      this.hooks = config
    }
  }

  /**
   * 注册 hooks
   */
  register(config: HookConfig): void {
    this.hooks = this.mergeHooks(this.hooks, config)
  }

  /**
   * 合并 hooks 配置
   */
  private mergeHooks(target: HookConfig, source: HookConfig): HookConfig {
    const result = { ...target }

    for (const key of Object.keys(source) as (keyof HookConfig)[]) {
      if (source[key]) {
        result[key] = {
          ...target[key],
          ...source[key]
        } as HookConfig[typeof key]
      }
    }

    return result
  }

  /**
   * 执行 hook
   * @param path hook 路径，如 'auth.beforeLogin'
   * @param data 传入数据
   * @returns 处理后的数据
   */
  async call<T, R = T>(path: string, data: T): Promise<R> {
    const [module, hookName] = path.split('.') as [keyof HookConfig, string]
    const moduleHooks = this.hooks[module]

    if (!moduleHooks) {
      return data as unknown as R
    }

    const hook = (moduleHooks as Record<string, HookFn<T, R> | undefined>)[hookName]

    if (!hook) {
      return data as unknown as R
    }

    try {
      const result = await hook(data)
      return result ?? (data as unknown as R)
    } catch (error) {
      console.error(`[HookManager] Error in hook ${path}:`, error)
      throw error
    }
  }

  /**
   * 安全执行 hook（不抛出错误）
   */
  async callSafe<T, R = T>(path: string, data: T): Promise<R> {
    try {
      return await this.call<T, R>(path, data)
    } catch {
      return data as unknown as R
    }
  }

  /**
   * 检查 hook 是否存在
   */
  has(path: string): boolean {
    const [module, hookName] = path.split('.') as [keyof HookConfig, string]
    const moduleHooks = this.hooks[module]

    if (!moduleHooks) {
      return false
    }

    return hookName in moduleHooks
  }

  /**
   * 获取所有已注册的 hooks
   */
  getHooks(): HookConfig {
    return { ...this.hooks }
  }

  /**
   * 清除所有 hooks
   */
  clear(): void {
    this.hooks = {}
  }
}

/**
 * 创建 HookManager 实例
 */
export function createHookManager(config?: HookConfig): HookManager {
  return new HookManager(config)
}
